import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Video, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared call queue panel used by both the doctor dashboard and the
 * pharmacist dashboard. Fetches /api/calls/queue every 10s, exposes
 * Accept-and-Join for waiting calls and Join for active ones. Routes
 * the provider to /doctor/call/:id (works for both doctors and
 * pharmacists since the route is now role-open).
 *
 * Props:
 *  - onCountsChange (optional): called with {active, waiting} whenever
 *    the local counts change. Lets the parent render stat cards.
 */
export const CallQueuePanel = ({ onCountsChange }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [callQueue, setCallQueue] = useState([]);

  useEffect(() => {
    const fetchQueue = async () => {
      try {
        const r = await axios.get(`${API}/calls/queue`, { withCredentials: true });
        setCallQueue(r.data || []);
        if (onCountsChange) {
          const list = r.data || [];
          onCountsChange({
            active: list.filter((c) => c.status === 'active').length,
            waiting: list.filter((c) => c.status === 'waiting').length,
          });
        }
      } catch (_e) {
        // silent — the parent already handles missing-permission cases
      }
    };
    fetchQueue();
    const iv = setInterval(fetchQueue, 10000);
    return () => clearInterval(iv);
  }, [onCountsChange]);

  const handleAccept = async (callId) => {
    try {
      await axios.post(`${API}/calls/${callId}/accept`, {}, { withCredentials: true });
      navigate(`/doctor/call/${callId}`);
    } catch (_e) {
      console.error('accept failed', _e);
    }
  };

  const handleJoin = (callId) => navigate(`/doctor/call/${callId}`);

  if (!callQueue.length) {
    return (
      <Card className="p-6" data-testid="call-queue-empty">
        <p className="text-gray-600">{t('لا توجد مكالمات واردة', 'No incoming calls', 'Aucun appel')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="call-queue-list">
      {callQueue.map((call) => (
        <Card key={call._id} className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold">{call.patient_name}</h3>
              <p className="text-sm text-gray-600 flex items-center gap-1">
                {call.call_type === 'video' ? <Video size={14} /> : <Phone size={14} />}
                {call.call_type === 'video'
                  ? t('فيديو', 'Video', 'Vidéo')
                  : t('صوت', 'Voice', 'Voix')}
              </p>
              {call.medical_form?.current_symptoms && (
                <p className="text-xs text-gray-500 mt-1">{call.medical_form.current_symptoms}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs ${
                  call.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
                data-testid={`call-status-${call._id}`}
              >
                {call.status}
              </span>
              {call.status === 'waiting' && (
                <Button
                  size="sm"
                  onClick={() => handleAccept(call._id)}
                  className="bg-[#1e88e5] hover:bg-[#1565c0]"
                  data-testid={`accept-call-${call._id}`}
                >
                  {t('قبول وانضمام', 'Accept & Join', 'Accepter')}
                </Button>
              )}
              {call.status === 'active' && (
                <Button
                  size="sm"
                  onClick={() => handleJoin(call._id)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`join-call-${call._id}`}
                >
                  <Video size={14} className={language === 'ar' ? 'ml-1' : 'mr-1'} />
                  {t('انضمام', 'Join', 'Rejoindre')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default CallQueuePanel;
